/** @jsxImportSource preact */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import type { JSX } from 'preact'
import type { SaleRecord } from '@plt/protect-group-client'
import { callApi } from '../api'
import { config } from '../config'
import { useFrontContext, extractBookingId } from '../providers/front'

interface Policy {
  quoteId: string | null
  saleId: string | null
  cancelAttemptedAt: string | null
  cancelledAt: string | null
}

/** Their record trails an accepted write by a few seconds — measured between under two and about
 *  twenty — so a write says what it expects to see and the panel settles on its own. */
const SETTLE_EVERY_MS = 2_000
const SETTLE_DEADLINE_MS = 24_000

/** What an accepted write expects to find in their record once it catches up. */
interface Expected {
  eventDate?: string
  cancelled?: true
}

const met = (expected: Expected, read: { record: SaleRecord; whollyCancelled: boolean }): boolean =>
  (!expected.eventDate || travelDateTime(read.record)?.startsWith(expected.eventDate) === true) &&
  // Their server-computed flag, not `isCancelled`: a sale cancels item by item, and this is the
  // rule `sync` and the card already use.
  (!expected.cancelled || read.whollyCancelled)

type TheirRecord =
  | { state: 'loading' }
  | { state: 'read'; record: SaleRecord | null; raw: unknown; whollyCancelled: boolean }
  | { state: 'failed' }

/** Mirrors the durable operation's result, so a branch on `outcome` narrows what it carries. */
type Outcome =
  | { outcome: 'not-covered' }
  | { outcome: 'no-sale' }
  | { outcome: 'synced'; saleId: string; recorded: boolean }
  | { outcome: 'already-cancelled'; saleId: string; cancelledAt: string }
  | { outcome: 'stale'; saleId: string }
  | { outcome: 'unconfirmed'; saleId: string; attemptedAt?: string }
  | { outcome: 'cancelled'; saleId: string; recorded: boolean }

export function RefundProtect(): JSX.Element {
  const [bookingId, setBookingId] = useState('')
  // Front rewrites `bookingId` whenever the agent switches conversation, so it is never safe to
  // act on. `subject` is the booking the displayed policy belongs to, and every call keys on it.
  const [subject, setSubject] = useState('')
  const [policy, setPolicy] = useState<Policy | null | undefined>(undefined)
  const [looking, setLooking] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [reason, setReason] = useState('')
  const [theirRecord, setTheirRecord] = useState<TheirRecord | null>(null)
  // One slot per field: a cancellation and a date change can both be outstanding, while a second
  // date change supersedes the first rather than being required alongside it.
  const [settling, setSettling] = useState<Expected | null>(null)
  const expect = (what: Expected) => setSettling(prev => ({ ...prev, ...what }))

  // Read inside async callbacks, where the closed-over values are already stale.
  const subjectRef = useRef(subject)
  subjectRef.current = subject
  const bookingRef = useRef(bookingId)
  bookingRef.current = bookingId
  // Two reads of the same booking can be in flight; without this the slower one wins.
  const saleRequest = useRef(0)
  // Bumps first, so a read already in flight cannot repopulate what it just cleared.
  const forgetTheirRecord = () => {
    saleRequest.current += 1
    setTheirRecord(null)
    setSettling(null)
  }

  const frontContext = useFrontContext()

  useEffect(() => {
    if (frontContext) {
      setBookingId(extractBookingId(frontContext) ?? '')
    }
  }, [frontContext])

  useEffect(() => {
    if (subject && bookingId.trim() !== subject) {
      setSubject('')
      setPolicy(undefined)
      setOutcome(null)
      setConfirming(false)
      forgetTheirRecord()
    }
  }, [bookingId, subject])

  const readPolicy = async (reference: string) => {
    const body = await callApi<{ policy: Policy | null }>(
      `/bookings/${reference}/refund-protect`,
      {},
      'Failed to look up Refund Protect.',
    )
    return body.policy
  }

  // Not awaited by the caller: a timeout must cost this panel only.
  const readSale = async (reference: string, quiet = false) => {
    const generation = ++saleRequest.current
    const superseded = () => generation !== saleRequest.current || reference !== bookingRef.current.trim()
    if (!quiet) setTheirRecord({ state: 'loading' })
    try {
      const body = await callApi<{ record: SaleRecord | null; raw: unknown; whollyCancelled: boolean }>(
        `/bookings/${reference}/refund-protect/sale`,
        {},
        'Failed to read the sale at Protect Group.',
      )
      if (superseded()) return
      // Only an explicit read may remove the panel; a poll finding nothing leaves it standing.
      if (body.record === null && body.raw === null) return quiet ? undefined : setTheirRecord(null)
      setTheirRecord({ state: 'read', record: body.record, raw: body.raw, whollyCancelled: body.whollyCancelled })
    } catch {
      // A poll that fails leaves the record standing; only an explicit read may report the failure.
      if (superseded() || quiet) return
      setTheirRecord({ state: 'failed' })
    }
  }

  useEffect(() => {
    if (!settling) return
    let stopped = false
    let timer: ReturnType<typeof setTimeout>
    // Chained rather than an interval: a read of theirs takes about as long as the gap between
    // ticks, and a second one would invalidate the first by generation.
    const tick = async () => {
      await readSale(subjectRef.current, true)
      if (stopped) return
      timer = setTimeout(() => void tick(), SETTLE_EVERY_MS)
    }
    timer = setTimeout(() => void tick(), SETTLE_EVERY_MS)
    // Independent of the chain, so a read that never resolves cannot hold the panel here. Giving
    // up leaves the disagreement on screen, which is real, rather than claiming to still be busy.
    // Only its own expectation, though: cleanup is queued after paint, so a write accepted close
    // to this deadline can be cleared by the timer it replaced.
    const giveUp = setTimeout(() => setSettling(current => (current === settling ? null : current)), SETTLE_DEADLINE_MS)
    return () => {
      stopped = true
      clearTimeout(timer)
      clearTimeout(giveUp)
    }
  }, [settling])

  useEffect(() => {
    if (!settling || theirRecord?.state !== 'read' || !theirRecord.record) return
    if (met(settling, { record: theirRecord.record, whollyCancelled: theirRecord.whollyCancelled })) setSettling(null)
  }, [settling, theirRecord])

  const lookUp = async (e: JSX.TargetedEvent<HTMLFormElement>) => {
    e.preventDefault()

    const reference = bookingId.trim()

    if (!reference) {
      setErrorMessage('Booking ID is required')
      return
    }

    if (busy) {
      return
    }

    setLooking(true)
    setErrorMessage('')
    setSubject('')
    setPolicy(undefined)
    setOutcome(null)
    setConfirming(false)
    setReason('')
    forgetTheirRecord()

    try {
      const found = await readPolicy(reference)
      if (reference !== bookingRef.current.trim()) return
      setPolicy(found)
      setSubject(reference)
      if (found?.saleId) void readSale(reference)
    } catch (error) {
      if (reference !== bookingRef.current.trim()) return
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.')
    } finally {
      setLooking(false)
    }
  }

  const runOperation = async (operation: 'sync' | 'cancel', body?: object) => {
    const actedOn = subject

    setBusy(true)
    setErrorMessage('')
    setOutcome(null)

    try {
      const result = await callApi<Outcome>(
        `/bookings/${actedOn}/refund-protect/${operation}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body ?? {}),
        },
        'Failed to reach Protect Group.',
      )
      if (actedOn !== subjectRef.current) return
      setOutcome(result)
      setConfirming(false)
      if (result.outcome === 'cancelled') expect({ cancelled: true })

      // An operation can change our record, so read it back instead of inferring the new state.
      try {
        const refreshed = await readPolicy(actedOn)
        if (actedOn !== subjectRef.current) return
        setPolicy(refreshed)
        if (refreshed?.saleId) void readSale(actedOn)
        // A policy with no sale has nothing of theirs to show.
        else forgetTheirRecord()
      } catch {
        // A failed re-read is not a failed operation, and the outcome already says what happened.
      }
    } catch (error) {
      if (actedOn !== subjectRef.current) return
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.')
    } finally {
      setBusy(false)
    }
  }

  // Rejects rather than reporting into the page: the editor owns this failure, so a bad date does
  // not clear the panel or read as the booking having failed to load. No re-read follows: their
  // store trails the call by up to half a minute, so it would only redraw the old date, and
  // switching the panel to `loading` would unmount the editor before it could say anything.
  const moveEventDate = async (eventDate: string): Promise<void> => {
    const actedOn = subject
    // Marks the panel busy for the same reason the operations do: nothing else should be sent to
    // them while this is in flight.
    setBusy(true)
    try {
      await callApi(
        `/bookings/${actedOn}/refund-protect/event-date`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ eventDate }) },
        'Failed to reach Protect Group.',
      )
      if (actedOn !== subjectRef.current) return
      expect({ eventDate })
    } finally {
      setBusy(false)
    }
  }

  const cancelled =
    Boolean(policy?.cancelledAt) ||
    outcome?.outcome === 'cancelled' ||
    outcome?.outcome === 'already-cancelled' ||
    (outcome?.outcome === 'synced' && outcome.recorded)
  // An attempt with no confirmed answer: cancelling again could cancel twice, so only a human
  // checking Protect Group can move it on.
  const unconfirmed = (Boolean(policy?.cancelAttemptedAt) && !cancelled) || outcome?.outcome === 'unconfirmed'
  // They already hold it cancelled while we do not: `Sync` is the move, not another DELETE.
  const theirsCancelled = theirRecord?.state === 'read' && theirRecord.whollyCancelled

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <div className="mb-4">
        <a
          href={config.routePrefix + '/'}
          className="text-indigo-600 hover:text-indigo-800"
        >
          &larr; Dashboard
        </a>
      </div>

      <h1 className="text-2xl font-bold text-gray-800 mb-6">Refund Protect</h1>

      <form
        onSubmit={lookUp}
        className="mb-6"
      >
        <label
          className="block text-sm font-medium text-gray-700 mb-1"
          htmlFor="bookingId"
        >
          Booking ID
        </label>
        <div className="flex gap-2">
          <input
            id="bookingId"
            type="text"
            value={bookingId}
            onInput={e => setBookingId(e.currentTarget.value)}
            placeholder="PLT123456"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900"
          />
          <button
            type="submit"
            disabled={looking || busy}
            className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 disabled:bg-indigo-400"
          >
            {looking ? 'Looking up...' : 'Look up'}
          </button>
        </div>
      </form>

      {errorMessage && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{errorMessage}</div>}

      {policy === null && (
        <div className="p-4 border border-gray-200 rounded-lg text-gray-600">
          This booking has no Refund Protect policy. The customer did not opt in.
        </div>
      )}

      {policy && (
        <div className="p-4 border border-gray-200 rounded-lg">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            Policy for <span className="font-mono">{subject}</span>
          </h2>
          <dl className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
            <Field label="Quote ID">
              <span className="font-mono">{policy.quoteId ?? '\u2014'}</span>
            </Field>
            <Field label="Sale ID">
              {policy.saleId ? (
                <span className="font-mono">{policy.saleId}</span>
              ) : (
                <span className="text-amber-600">not yet confirmed</span>
              )}
            </Field>
            <Field label="Status">
              {cancelled ? (
                <span className="text-red-700">Cancelled</span>
              ) : unconfirmed ? (
                <span className="text-amber-700">Cancellation attempted, unconfirmed</span>
              ) : (
                <span className="text-green-700">Live</span>
              )}
            </Field>
          </dl>

          {!policy.saleId && (
            <p className="mt-3 text-sm text-gray-600">
              The sale has not been registered with Protect Group, so there is nothing to cancel yet.
            </p>
          )}

          {unconfirmed && (
            <p className="mt-3 text-sm text-amber-700">
              A cancellation was already attempted for this policy and Protect Group never confirmed it. Check their
              dashboard before trying again — cancelling now could cancel it twice.
            </p>
          )}

          {policy.saleId && (
            <div className="mt-4 flex gap-2">
              {/* Its only write is guarded by our own `cancelled_at`, so once that is set the
                  operation can do nothing but spend an execution saying so. */}
              {!cancelled && (
                <button
                  onClick={() => runOperation('sync')}
                  disabled={busy}
                  className="px-4 py-2 border border-indigo-600 text-indigo-600 font-medium rounded-md hover:bg-indigo-50 disabled:opacity-50"
                >
                  {busy ? 'Working...' : 'Sync with Protect Group'}
                </button>
              )}

              {!cancelled && !confirming && (
                <button
                  onClick={() => setConfirming(true)}
                  // Disabled rather than hidden, so the guard against a second cancellation reads
                  // as deliberate.
                  disabled={unconfirmed || theirsCancelled}
                  className="px-4 py-2 border border-red-600 text-red-600 font-medium rounded-md hover:bg-red-50 disabled:opacity-50 disabled:hover:bg-transparent"
                >
                  Cancel policy
                </button>
              )}
            </div>
          )}

          {confirming && (
            <div className="mt-4 p-4 border border-red-200 bg-red-50 rounded-md">
              <p className="text-sm text-red-800 font-medium mb-1">Cancel this policy at Protect Group?</p>
              <p className="text-sm text-red-700 mb-3">
                This cannot be undone from here. The customer loses their refund protection.
              </p>

              {theirsCancelled && (
                <p className="text-sm text-amber-800 mb-3">
                  Protect Group already hold this policy as cancelled, so there is nothing to cancel. Sync with Protect
                  Group instead to record it here.
                </p>
              )}

              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor="cancellationReason"
              >
                Reason (optional)
              </label>
              <input
                id="cancellationReason"
                type="text"
                value={reason}
                onInput={e => setReason(e.currentTarget.value)}
                maxLength={500}
                placeholder="Booking cancelled before departure"
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 mb-3"
              />

              <div className="flex gap-2">
                <button
                  onClick={() =>
                    runOperation('cancel', {
                      saleId: policy.saleId,
                      ...(reason.trim() ? { reason: reason.trim() } : {}),
                    })
                  }
                  disabled={busy || theirsCancelled}
                  className="px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 disabled:bg-red-400"
                >
                  {busy ? 'Cancelling...' : 'Yes, cancel the policy'}
                </button>
                <button
                  onClick={() => setConfirming(false)}
                  disabled={busy}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 disabled:opacity-50"
                >
                  Keep it
                </button>
              </div>
            </div>
          )}

          {theirRecord && (
            <TheirRecordPanel
              theirRecord={theirRecord}
              settling={settling !== null}
              ourCancelledAt={policy.cancelledAt}
              onReread={() => void readSale(subject)}
              onMoveEventDate={moveEventDate}
            />
          )}

          {outcome && <OutcomeReport outcome={outcome} />}
        </div>
      )}
    </div>
  )
}

/** A `javascript:` or `data:` URL executes on click, so only http(s) is offered as a link. */
const asWebUrl = (value: string): string | null => {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null
  } catch {
    return null
  }
}

interface CopyLinkProps {
  url: string
}

function CopyLink({ url }: CopyLinkProps): JSX.Element {
  const [copied, setCopied] = useState(false)
  const [failed, setFailed] = useState(false)
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => {
          // Absent outside a secure context, and reading `.writeText` off it would throw before
          // there is a promise to catch.
          if (!navigator.clipboard) return setFailed(true)
          void navigator.clipboard
            .writeText(url)
            .then(() => setCopied(true))
            .catch(() => setFailed(true))
        }}
        className="px-2 py-1 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-100"
      >
        {failed ? 'Copy failed' : copied ? 'Copied' : 'Copy link'}
      </button>
      {/* Without target/rel this navigates the Front iframe away and the agent loses the app. */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-indigo-600 hover:text-indigo-800"
      >
        Open
      </a>
    </span>
  )
}

interface RawRecordProps {
  raw: unknown
}

function RawRecord({ raw }: RawRecordProps): JSX.Element {
  const text = useMemo(() => JSON.stringify(raw, null, 2), [raw])
  return (
    <details className="mt-3">
      <summary className="text-xs text-gray-500 cursor-pointer">Raw record</summary>
      <pre className="mt-2 text-xs text-gray-700 bg-gray-50 p-3 rounded overflow-x-auto">{text}</pre>
    </details>
  )
}

interface FieldProps {
  label: string
  children: JSX.Element | string
}

function Field({ label, children }: FieldProps): JSX.Element {
  return (
    <>
      <dt className="text-gray-500">{label}</dt>
      <dd className="col-span-2 text-gray-800">{children}</dd>
    </>
  )
}

const money = (value: number, currency: string) => `${currency} ${value.toFixed(2)}`

interface TheirRecordPanelProps {
  theirRecord: TheirRecord
  settling: boolean
  ourCancelledAt: string | null
  onReread: () => void
  onMoveEventDate: (eventDate: string) => Promise<void>
}

function TheirRecordPanel({
  theirRecord,
  settling,
  ourCancelledAt,
  onReread,
  onMoveEventDate,
}: TheirRecordPanelProps): JSX.Element {
  return (
    <div className="mt-4 border-t border-gray-200 pt-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-800">Protect Group's record</h3>
        {settling ? (
          <span
            role="status"
            className="text-xs text-gray-500"
          >
            Confirming with Protect Group...
          </span>
        ) : (
          theirRecord.state !== 'loading' && (
            <button
              type="button"
              onClick={onReread}
              className="text-xs text-indigo-600 hover:text-indigo-800"
            >
              Check again
            </button>
          )
        )}
      </div>
      {theirRecord.state === 'loading' ? (
        <RecordSkeleton />
      ) : theirRecord.state === 'failed' ? (
        <p className="text-sm text-amber-700">Could not read their record.</p>
      ) : (
        <SaleFields
          record={theirRecord.record}
          raw={theirRecord.raw}
          ourCancelledAt={ourCancelledAt}
          onMoveEventDate={onMoveEventDate}
        />
      )}
    </div>
  )
}

/** Stands in for a loaded record at the same height, so the card does not resize under the agent
 *  when one arrives. `h-5` matches the line box of the `text-sm` rows it replaces. */
function RecordSkeleton(): JSX.Element {
  return (
    <div
      role="status"
      aria-label="Reading their record"
      className="animate-pulse space-y-2"
    >
      {Array.from({ length: 6 }, (_, row) => (
        <div
          key={row}
          className="grid grid-cols-3 gap-x-4"
        >
          <div className="h-5 w-28 rounded bg-gray-100" />
          <div className="col-span-2 h-5 w-48 rounded bg-gray-100" />
        </div>
      ))}
      <div className="h-5 w-24 rounded bg-gray-100" />
    </div>
  )
}

interface SaleFieldsProps {
  record: SaleRecord | null
  raw: unknown
  ourCancelledAt: string | null
  onMoveEventDate: (eventDate: string) => Promise<void>
}

/** Their value is a naive local datetime; `datetime-local` wants the same shape without seconds. */
const asInputValue = (eventDate?: string | null): string => (eventDate ?? '').slice(0, 16)

/** The sale reports this date at midnight; its items carry the departure time, and the time sent
 *  is written back to them. Seeding from the sale would replace a real departure time with 00:00. */
const travelDateTime = (record: SaleRecord): string | null | undefined =>
  record.items?.find(item => item.eventDate && item.cancelled !== true)?.eventDate ?? record.eventDate

/** Advisory only: the picker refuses these, a typed value can still slip past, and the route is
 *  what actually enforces them. */
const notBefore = (): string => {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)
  return `${local.toISOString().slice(0, 10)}T00:00`
}

/** Protect Group refuse a move of more than 14 days past the date they currently hold. */
const notAfter = (eventDate?: string | null): string | undefined => {
  const day = (eventDate ?? '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined
  const cap = new Date(`${day}T00:00:00Z`)
  cap.setUTCDate(cap.getUTCDate() + 14)
  return `${cap.toISOString().slice(0, 10)}T23:59`
}

interface EventDateProps {
  eventDate?: string | null
  editable: boolean
  onMove: (eventDate: string) => Promise<void>
}

function EventDate({ eventDate, editable, onMove }: EventDateProps): JSX.Element {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(asInputValue(eventDate))
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState('')

  if (!editing) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2">
        <span className="font-mono">{eventDate ?? '\u2014'}</span>
        {/* `p-0` because app.css pads every button, and this one is a text link. */}
        {editable && (
          <button
            type="button"
            onClick={() => {
              // Seeded on open, not on render, so a re-read while editing cannot move the field
              // under the agent.
              setValue(asInputValue(eventDate))
              setFailed('')
              setEditing(true)
            }}
            className="p-0 text-xs text-indigo-600 hover:text-indigo-800"
          >
            Change
          </button>
        )}
      </span>
    )
  }

  return (
    <span className="block">
      <span className="flex flex-wrap items-center gap-2">
        <input
          type="datetime-local"
          aria-label="Event date"
          aria-describedby="event-date-note"
          value={value}
          min={notBefore()}
          max={notAfter(eventDate)}
          disabled={saving}
          onInput={event => setValue((event.target as HTMLInputElement).value)}
          className="w-auto px-2 py-1 border border-gray-300 rounded bg-white text-sm text-gray-800 disabled:opacity-50"
        />
        <button
          type="button"
          disabled={saving || !value || value === asInputValue(eventDate)}
          onClick={() => {
            setSaving(true)
            setFailed('')
            onMove(value)
              .then(() => setEditing(false))
              .catch((error: unknown) => setFailed(error instanceof Error ? error.message : 'Could not save.'))
              .finally(() => setSaving(false))
          }}
          className="px-2 py-1 text-xs border border-indigo-600 text-indigo-600 rounded hover:bg-indigo-50 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => setEditing(false)}
          className="px-2 py-1 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
      </span>
      {failed ? (
        <span
          role="alert"
          id="event-date-note"
          className="mt-1 block text-xs text-amber-700"
        >
          {failed}
        </span>
      ) : (
        notAfter(eventDate) && (
          <span
            id="event-date-note"
            className="mt-1 block text-xs text-gray-500"
          >
            Protect Group allow up to 14 days after the current event date.
          </span>
        )
      )}
    </span>
  )
}

/** Their link, offered only when it is one we would let a browser follow. */
const refundForm = (link?: string | null): JSX.Element => {
  if (!link) return <>{'\u2014'}</>
  const safe = asWebUrl(link)
  return safe ? <CopyLink url={safe} /> : <span className="text-amber-700 break-all">{link}</span>
}

function SaleFields({ record, raw, ourCancelledAt, onMoveEventDate }: SaleFieldsProps): JSX.Element {
  if (!record) {
    return (
      <>
        <p className="text-sm text-amber-700">
          Their record did not match the shape we know, so it is shown as sent.
          {!ourCancelledAt && ' Their cancellation state could not be read — check it below yourself.'}
        </p>
        <RawRecord raw={raw} />
      </>
    )
  }

  const name = [record.customerFirstName, record.customerLastName].filter(Boolean).join(' ')

  return (
    <>
      {record.isCancelled && !ourCancelledAt && (
        <p className="mb-3 p-3 bg-amber-50 text-amber-800 text-sm rounded">
          Protect Group hold this policy as cancelled and we do not. Press Sync with Protect Group to record it.
        </p>
      )}

      {!record.isCancelled && ourCancelledAt && (
        <p className="mb-3 p-3 bg-gray-50 text-gray-700 text-sm rounded">
          We hold this policy as cancelled and they do not yet. They accept a cancellation before applying it, so their
          record trails ours for a few minutes. Use Check again to re-read it.
        </p>
      )}

      <dl className="grid grid-cols-3 gap-x-4 gap-y-2 text-sm">
        <Field label="Customer">{name || '\u2014'}</Field>
        <Field label="Cancelled with them">
          {record.isCancelled ? <span className="text-red-700">Yes</span> : <span className="text-green-700">No</span>}
        </Field>
        <Field label="Event date">
          {/* Either side holding it cancelled ends the cover, and they refuse to re-date a
              cancelled sale with a "Transaction not found" that reads like the sale is gone. */}
          <EventDate
            eventDate={travelDateTime(record)}
            editable={!ourCancelledAt && !record.isCancelled}
            onMove={onMoveEventDate}
          />
        </Field>
        <Field label="Total value">
          <span className="font-mono">{money(record.totalValue, record.currencyCode)}</span>
        </Field>
        <Field label="Total payment value">
          <span className="font-mono">{money(record.totalPaymentValue, record.currencyCode)}</span>
        </Field>
        <Field label="Refund form">{refundForm(record.refundableLink)}</Field>
      </dl>

      <RawRecord raw={raw} />
    </>
  )
}

interface OutcomeReportProps {
  outcome: Outcome
}

function OutcomeReport({ outcome }: OutcomeReportProps) {
  // The card reports this one from the policy, so it survives a fresh look-up.
  if (outcome.outcome === 'unconfirmed') return null

  return (
    <div className="mt-4 border-t border-gray-200 pt-4 text-sm">
      {outcome.outcome === 'not-covered' && <p className="text-gray-600">This booking has no Refund Protect policy.</p>}

      {outcome.outcome === 'no-sale' && (
        <p className="text-gray-600">The sale is not registered with Protect Group, so there is nothing to act on.</p>
      )}

      {outcome.outcome === 'stale' && (
        <p className="text-amber-700">
          This booking has a newer Refund Protect policy than the one shown, so nothing was cancelled. Look it up again.
        </p>
      )}

      {outcome.outcome === 'already-cancelled' && (
        <p className="text-gray-600">This policy was already cancelled. Nothing was sent to Protect Group.</p>
      )}

      {outcome.outcome === 'cancelled' && (
        <p className="text-green-700">
          Protect Group accepted the cancellation.
          {outcome.recorded === false && (
            <span className="text-amber-700">
              {' '}
              A cancellation was already recorded against this policy, so this one was not written over it.
            </span>
          )}
        </p>
      )}

      {outcome.outcome === 'synced' &&
        (outcome.recorded ? (
          <p className="text-green-700">Protect Group hold this policy as cancelled, and that is now recorded here.</p>
        ) : (
          <p className="text-gray-600">Nothing changed on our side.</p>
        ))}
    </div>
  )
}
